/**
 * A weighted decision matrix: options scored on criteria, criteria weighted by
 * importance, options ranked by their weighted totals.
 *
 * Weights are normalised to sum to 1, so raw weights (3, 2, 1) and percentages (50, 33,
 * 17) mean the same thing and the total stays on the score scale — a 4.2 on a 1–5 scale
 * reads as "4.2 out of 5".
 *
 * The sensitivity check asks the question a matrix is most often wrong about: the
 * weights are judgement calls, so does the winner survive a different judgement? Each
 * weight in turn is moved 20% up and 20% down with the others held fixed (then
 * everything is renormalised), and the winner is recomputed. The tipping point goes one
 * step further and solves for the exact change in that one weight at which another
 * option draws level.
 */

export interface DecisionInput {
  /** Raw criterion weights, ≥ 0, not all zero. */
  weights: readonly number[];
  /** scores[option][criterion]. */
  scores: readonly (readonly number[])[];
}

export interface WeightCheck {
  criterion: number;
  /** Winning option(s) with this weight × 0.8 and × 1.2. */
  downWinners: number[];
  upWinners: number[];
  /** Either change produces a different winner (or a tie). */
  changes: boolean;
  /**
   * The smallest relative change to this weight alone at which another option draws
   * level with the leader — +0.35 means "35% heavier" — or null if no change can do it.
   */
  tippingChange: number | null;
  /** The option that draws level at the tipping point. */
  tippingOption: number | null;
}

export interface DecisionResult {
  normalizedWeights: number[];
  totals: number[];
  /** Options in rank order with competition ranking (1, 1, 3 for a tie). */
  ranking: { option: number; rank: number; total: number }[];
  winners: number[];
  /** Leader minus runner-up; 0 when tied, null with one option. */
  margin: number | null;
  /** Null when the top is tied — the check needs a single leader. */
  checks: WeightCheck[] | null;
  /** No ±20% change in a single weight alters the winner. */
  stable: boolean | null;
}

/** Totals this close are a tie: floating-point noise, not a difference. */
const TIE_TOLERANCE = 1e-9;
const SWING = 0.2;

export function weightedTotals(
  weights: readonly number[],
  scores: readonly (readonly number[])[]
): { normalized: number[]; totals: number[] } {
  const sum = weights.reduce((s, w) => s + w, 0);
  const normalized = weights.map((w) => w / sum);
  const totals = scores.map((row) =>
    row.reduce((total, score, j) => total + normalized[j] * score, 0)
  );
  return { normalized, totals };
}

export function winnersOf(totals: readonly number[]): number[] {
  const best = Math.max(...totals);
  return totals.flatMap((t, i) => (best - t <= TIE_TOLERANCE ? [i] : []));
}

function rank(totals: readonly number[]) {
  const order = totals
    .map((total, option) => ({ option, total }))
    .sort((a, b) => b.total - a.total || a.option - b.option);
  return order.map((entry) => ({
    ...entry,
    rank:
      1 +
      order.filter((other) => other.total - entry.total > TIE_TOLERANCE).length,
  }));
}

function sameSet(a: readonly number[], b: readonly number[]): boolean {
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

/**
 * The relative change in weight j at which `challenger` ties `leader`.
 *
 * With every other weight fixed, each total is (Σₖ≠ⱼ rₖsₖ + x·sⱼ) / (R + x) in the
 * weight x of criterion j. Two such totals share the denominator, so they are equal
 * where the numerators are: x = (A_leader − A_challenger) / (s_challenger,j − s_leader,j).
 */
function crossing(
  input: DecisionInput,
  j: number,
  leader: number,
  challenger: number
): number | null {
  const partial = (option: number) =>
    input.scores[option].reduce(
      (sum, score, k) => (k === j ? sum : sum + input.weights[k] * score),
      0
    );
  const slope = input.scores[challenger][j] - input.scores[leader][j];
  if (slope === 0 || input.weights[j] === 0) {
    return null;
  }
  const x = (partial(leader) - partial(challenger)) / slope;
  const others = input.weights.reduce((s, w, k) => (k === j ? s : s + w), 0);
  // A weight cannot go below zero, and at zero with nothing else weighted there is
  // no total left to compare.
  if (!(x >= 0) || others + x <= 0) {
    return null;
  }
  return x / input.weights[j] - 1;
}

function tippingPoint(input: DecisionInput, j: number, leader: number) {
  let best: { change: number; option: number } | null = null;
  for (let option = 0; option < input.scores.length; option += 1) {
    if (option === leader) {
      continue;
    }
    const change = crossing(input, j, leader, option);
    if (
      change !== null &&
      (best === null || Math.abs(change) < Math.abs(best.change))
    ) {
      best = { change, option };
    }
  }
  return best;
}

function checkWeight(
  input: DecisionInput,
  j: number,
  winners: readonly number[]
): WeightCheck {
  const scaled = (factor: number) =>
    winnersOf(
      weightedTotals(
        input.weights.map((w, k) => (k === j ? w * factor : w)),
        input.scores
      ).totals
    );
  const downWinners = scaled(1 - SWING);
  const upWinners = scaled(1 + SWING);
  const tip = tippingPoint(input, j, winners[0]);
  return {
    criterion: j,
    downWinners,
    upWinners,
    changes: !(sameSet(downWinners, winners) && sameSet(upWinners, winners)),
    tippingChange: tip?.change ?? null,
    tippingOption: tip?.option ?? null,
  };
}

/** Null when the weights sum to zero or the matrix is empty. */
export function analyseDecision(input: DecisionInput): DecisionResult | null {
  const weightSum = input.weights.reduce((s, w) => s + w, 0);
  if (!(weightSum > 0) || input.scores.length === 0) {
    return null;
  }
  const { normalized, totals } = weightedTotals(input.weights, input.scores);
  const ranking = rank(totals);
  const winners = winnersOf(totals);
  const margin =
    ranking.length > 1 ? ranking[0].total - ranking[1].total : null;
  const single = winners.length === 1 && input.scores.length > 1;
  const checks = single
    ? input.weights.map((_, j) => checkWeight(input, j, winners))
    : null;
  return {
    normalizedWeights: normalized,
    totals,
    ranking,
    winners,
    margin: margin !== null && margin < TIE_TOLERANCE ? 0 : margin,
    checks,
    stable: checks ? checks.every((c) => !c.changes) : null,
  };
}
